import {
  If,
  Page,
  Form,
  Text,
  Ruler,
  Button,
  Unless,
  FormField,
} from '../../..'
import {
  addHomeValuation,
  setValuationHomeFactsProgress,
} from '../../../../actions/valuation'
import {
  selectHomeId,
  selectHomeUuid,
  selectValuationBounds,
} from '../../../../store/selectors/home'
import {
  AmplitudeContext,
  AmplitudeEventHelpers,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'
import {
  selectCustomerType,
  selectCustomerIsLoanOfficer,
} from '../../../../store/selectors/customerProfile'
import moment from 'moment'
import Outreach from './Outreach'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import OutreachOption from './OutreachOption'
import { FormattedMessage } from 'react-intl'
import { useUniqueId } from '../../../../hooks'
import { useSelector, useDispatch } from 'react-redux'
import { HeightFade } from '../../../shared/Transition'
import { KEY_CODES } from '../../../../constants/utility'
import DollarInput from '../../../shared/Input/DollarInput'
import { isTouchDevice } from '../../../../helpers/browser'
import { useTuneValue } from '../../../../hooks/useTuneValue'
import { fetchShowPmiModules } from '../../../../store/slices/pmi'
import { formatAbbrCurrency } from '../../../../helpers/formatters'
import { useState, useCallback, useEffect, useContext } from 'react'
import { redrawHeightFadeKey } from '../../../../actions/transitions'
import { updateHome, trackCMARequest } from '../../../../actions/home'
import { SlideOnMount, FadeOnMount } from '../../../shared/Animations'
import { selectClientFetchId } from '../../../../store/selectors/client'
import { selectHomeValue } from '../../../../store/selectors/home/metadata'
import { IconCheck, IconArrowLeft, IconArrowRight } from '../../../shared/Icon'
import withComposedClassName from '../../../../composables/withComposedClassName'
import { selectHasRecentlyRequested } from '../../../../store/selectors/home/cma'

import styles from './TuneValueForm.module.scss'
import panelStyles from '../../../shared/Panel/Panel.module.scss'

const Slide = props => <SlideOnMount interval={50} top='-80px' {...props} />
const Fade = props => <FadeOnMount delay={150} {...props} />

const DOLLARS = 'dollars'
const OUTREACH_OPTION = 'outreachOption'

const StyledDollarInput = withComposedClassName(styles.input)(DollarInput)

const TuneValueForm = ({
  defaultValue,
  defaultStart,
  defaultMin,
  defaultMax,
}) => {
  const dispatch = useDispatch()
  const { ampli } = useContext(AmplitudeContext)
  const clientId = useSelector(selectClientFetchId)
  const customerType = useSelector(selectCustomerType)
  const heightFadeId = useUniqueId('tuneValueHeightFade')
  const homeId = useSelector(selectHomeId)
  const homeUuid = useSelector(selectHomeUuid)
  const homeValue = useSelector(selectHomeValue) || defaultValue
  const bounds = useSelector(selectValuationBounds) || {
    homebotAmount: defaultStart,
    customerAmount: null,
    low: defaultMin,
    high: defaultMax,
  }
  const isLO = useSelector(selectCustomerIsLoanOfficer)
  const recentlyRequestedCMA = useSelector(selectHasRecentlyRequested)

  const {
    tuneValue,
    rulerValue,
    inputValue,
    inputFocused,
    setTuneValue,
    setRulerValue,
    setInputValue,
    setInputFocused,
    useStep,
    useMin,
    useMax,
    usePins,
    getValueFromEvent,
  } = useTuneValue({
    price: homeValue,
    useStep: 1000,
    bounds,
  })

  const [interacted, setInteracted] = useState(false)
  const [optedIn, setOptedIn] = useState(false)
  const useValue = parseInt(inputValue || tuneValue, 10)
  const isMaxValue = useValue === useMax
  const isMinValue = useValue === useMin
  const isMiddleValue = !(isMaxValue || isMinValue)
  const willSendCMA = optedIn || !isMiddleValue || !isLO

  const handleSubmit = useCallback(
    e => {
      e.preventDefault()

      dispatch(
        addHomeValuation(homeId, homeUuid, useValue, useValue !== homeValue)
      )

      const tuned_value_direction =
        useValue > homeValue
          ? AmplitudeEventConstants.TUNED_VALUE_INCREASE
          : AmplitudeEventConstants.TUNED_VALUE_DECREASE
      ampli?.valueTuned({ tuned_value_direction })

      dispatch(setValuationHomeFactsProgress(20))

      if (willSendCMA && !recentlyRequestedCMA) {
        if (isLO || useValue !== homeValue) {
          const hasRequestedCma = moment().utc().format()
          dispatch(updateHome({ hasRequestedCma }))
          dispatch(trackCMARequest())
          ampli?.clientRequestedInfo({
            request_type: AmplitudeEventConstants.CUSTOM_MARKET_ANALYSIS,
            customer_type:
              AmplitudeEventHelpers.getAmplitudeCustomerType(customerType),
          })
        }
      }

      dispatch(fetchShowPmiModules({ clientId: clientId, homeUuid: homeUuid }))
    },
    [
      isLO,
      ampli,
      homeId,
      clientId,
      dispatch,
      useValue,
      homeUuid,
      homeValue,
      willSendCMA,
      customerType,
      recentlyRequestedCMA,
    ]
  )

  useEffect(
    () => () => dispatch(redrawHeightFadeKey(heightFadeId)),
    [isMiddleValue, dispatch, heightFadeId]
  )

  return (
    <Page>
      <Page.Body className={styles.body}>
        <Form
          className={styles.form}
          fields={[{ name: DOLLARS, parse: DollarInput.parseValue }]}
          onSubmit={handleSubmit}
        >
          <Slide className={panelStyles.headWrap}>
            <Text size='xl' variant='neutralCool' variantCount='900'>
              <FormattedMessage
                id='TYV.valuation.ruler.head'
                defaultMessage='Why tune your value?'
              />
            </Text>
            <Text variant='neutralCool' variantCount='700' size='s'>
              <FormattedMessage
                id='TYV.valuation.ruler.subhead'
                defaultMessage='Your home value is an estimate driven by Homebot’s data models and market trends. Fine tune it to optimize your home report:'
              />
            </Text>
          </Slide>
          <Slide className={styles.rulerWrapEscape}>
            <div className={styles.rulerWrap}>
              <div className={styles.valueDisplay}>
                <FormField
                  input={StyledDollarInput}
                  hideLabel
                  value={inputValue || tuneValue}
                  name={DOLLARS}
                  onMouseOver={() => setInteracted(true)}
                  onFocus={() => setInputFocused(true)}
                  onBlur={e => {
                    const value = getValueFromEvent(e)
                    if (value && value !== inputValue) {
                      const rounded = Math.round(value / useStep) * useStep
                      if (rulerValue !== rounded) {
                        // new ruler value: set to null to force scroll behavior
                        setRulerValue(null)
                      }

                      setInputValue(null)
                      setTuneValue(`${value}`) // Bit of a hack, but a string value displays fine while also failing any direct === comparisons ensuring at least one onChange pass when the ruler slides
                    }
                    setInputFocused(false)
                  }}
                  onKeyDown={e => {
                    e.stopPropagation()
                    if ((e.which || e.keyCode) === KEY_CODES.enter) {
                      e.target.blur()
                    }
                  }}
                >
                  <FormattedMessage
                    id='TYV.input.value'
                    defaultMessage='Your home value'
                  />
                </FormField>
                <Button
                  tabIndex='-1'
                  type='button'
                  className={classnames(
                    styles.confirm,
                    inputFocused && styles.showConfirm
                  )}
                  iconStart={IconCheck}
                />
              </div>
              {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
              <div
                className={styles.rulerEscape}
                onMouseOver={() => setInteracted(true)}
              >
                <Ruler
                  className={styles.ruler}
                  value={rulerValue || tuneValue}
                  min={useMin}
                  max={useMax}
                  step={useStep}
                  pins={usePins}
                  minorTick={useStep * 5}
                  majorTick={useStep * 10}
                  intervalWidth={8}
                  labelRenderFunc={formatAbbrCurrency}
                  onChange={e => {
                    const value = getValueFromEvent(e)
                    if (value) {
                      setRulerValue(value)
                      if (inputValue !== null) {
                        // above condition allows masking incremental changes until useEffect hook above finds inputValue is relevant again
                        setInputValue(value)
                        setTuneValue(value)
                      }
                    }
                  }}
                />
              </div>
              <div className={styles.legend}>
                <div className={styles.left}>
                  <Text
                    size='s'
                    variant='neutralCool'
                    variantCount='700'
                    className={styles.uppercase}
                  >
                    <FormattedMessage
                      id='TYV.valuation.ruler.legend.low'
                      defaultMessage='Low range'
                    />
                  </Text>
                  <Text
                    size='l'
                    variant='neutralCool'
                    variantCount='700'
                    weight='semibold'
                  >
                    {formatAbbrCurrency(useMin)}
                  </Text>
                </div>
                <Text
                  variant='neutralCool'
                  variantCount='600'
                  weight='semibold'
                  className={classnames(
                    styles.directions,
                    interacted && styles.hide,
                    isTouchDevice() && styles.skip
                  )}
                >
                  <IconArrowLeft />
                  <FormattedMessage
                    id='TYV.valuation.ruler.info'
                    defaultMessage='Drag left or right to adjust value'
                  />
                  <IconArrowRight />
                </Text>
                <div className={styles.right}>
                  <Text
                    size='s'
                    variant='neutralCool'
                    variantCount='700'
                    className={styles.uppercase}
                  >
                    <FormattedMessage
                      id='TYV.valuation.ruler.legend.high'
                      defaultMessage='High range'
                    />
                  </Text>
                  <Text
                    size='l'
                    variant='neutralCool'
                    variantCount='700'
                    weight='semibold'
                  >
                    {formatAbbrCurrency(useMax)}
                  </Text>
                </div>
              </div>
            </div>
          </Slide>
          <Slide className={styles.outreachWrap}>
            <HeightFade id={heightFadeId} className={styles.outreach}>
              <If condition={isLO && isMiddleValue}>
                <OutreachOption
                  name={OUTREACH_OPTION}
                  className={styles.outreachOption}
                  amount={useValue}
                  extreme={!isMiddleValue}
                  defaultChecked={optedIn}
                  atHigh={isMaxValue}
                  atLow={isMinValue}
                  onChange={check => setOptedIn(check)}
                />
              </If>
              <Unless condition={isLO && isMiddleValue}>
                <Outreach
                  amount={useValue}
                  extreme={!isMiddleValue}
                  high={isMaxValue}
                  low={isMinValue}
                />
              </Unless>
            </HeightFade>
          </Slide>
          <div className={styles.flexFill} />
          <Fade className={panelStyles.submitWrap}>
            <Button
              primary
              fullWidth
              className={styles.submit}
              disabled={inputFocused}
              onClick={handleSubmit}
            >
              <If condition={inputFocused}>
                <FormattedMessage
                  id='TYV.valuation.button.submit.disabled'
                  defaultMessage='Confirm value above to continue'
                />
              </If>
              <Unless condition={inputFocused}>
                <If condition={isLO && isMiddleValue}>
                  <FormattedMessage
                    id='TYV.valuation.button.lo.reachout.submit'
                    defaultMessage='Use {value}'
                    values={{
                      value: formatAbbrCurrency(useValue),
                    }}
                  />
                </If>
                <Unless condition={isLO && isMiddleValue}>
                  <FormattedMessage
                    id='TYV.valuation.button.lo.submit'
                    defaultMessage='Continue'
                  />
                </Unless>
              </Unless>
            </Button>
          </Fade>
        </Form>
      </Page.Body>
    </Page>
  )
}

TuneValueForm.propTypes = {
  defaultValue: PropTypes.number,
  defaultStart: PropTypes.number,
  defaultMin: PropTypes.number,
  defaultMax: PropTypes.number,
}

export default TuneValueForm
